import { type ReactElement } from "react";
import type { TagsParams } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { TagsTrendPill } from "./TagsTrendPill.js";

const FIELDS = [
  { key: "trend", label: "Trend", fullWidth: true },
  { key: "actual", label: "Actual", fullWidth: false },
  { key: "standard", label: "Standard", fullWidth: false },
  { key: "gap", label: "Gap", fullWidth: true },
] as const;

export function TagsWidget({
  data,
}: WidgetBodyProps<TagsParams>): ReactElement {
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto"
      role="region"
      aria-label="TAGS details"
      // Keyboard users must be able to scroll long text within the canvas card.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <dl className="grid grid-cols-2 gap-2">
        <div className="col-span-2 min-w-0 pb-1">
          <dt className="text-xs text-pb-text-muted">Metric</dt>
          <dd className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold text-pb-text [overflow-wrap:anywhere]">
            {data.metric.trim() || "Metric not set"}
          </dd>
        </div>
        {FIELDS.map(({ key, label, fullWidth }) => (
          <div
            key={key}
            className={`flex min-w-0 gap-1.5 rounded-md border border-pb-border bg-pb-card-elevated/60 px-3 py-2.5 ${fullWidth ? "col-span-2" : ""} ${key === "trend" ? "items-center justify-between gap-3" : "flex-col"}`}
          >
            <dt className="shrink-0 text-xs font-medium text-pb-text-muted">
              {label}
            </dt>
            <dd
              className={`min-w-0 whitespace-pre-wrap break-words text-sm font-medium leading-relaxed text-pb-text [overflow-wrap:anywhere] ${key === "trend" ? "text-end" : ""}`}
            >
              {key === "trend" ? (
                // Trend is a classification, not free text: render the shared
                // tone + label pill so the canvas reads the value at a glance.
                <TagsTrendPill trend={data.trend} />
              ) : data[key].trim() === "" ? (
                <span className="font-normal italic text-pb-text-muted">
                  Not set
                </span>
              ) : (
                data[key]
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
